import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable, DataTableSearchConfig } from '@/components/DataTable';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ProcessDetails from './processDetails/ProcessDetails';
import { OneLineText } from '@/components/OneLineText';
import { ColumnDef } from '@tanstack/react-table';
import { ChevronDown, ChevronUp, Download, Edit3, Ellipsis, FilterIcon, Flag, MessageSquarePlus } from 'lucide-react';
import { getProcesses } from '@/services/Network';
import { convertISODateToMMDDYYYY, displayFullName } from '@/lib/utils';
import PeCommentsDialog from './PeCommentsDialog';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import { FormProvider, useForm } from 'react-hook-form';
import axios from 'axios';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { exportProcessServiceWorkOrder } from '@/services/exportProcessServiceWorkOrder';
import { usePatchWorkOrder } from '@/services/pe';
import { WORK_ORDER_STATUS_OPTIONS } from '@/utils/constant';
import { useFilterStore } from '@/stores/filterStore';
import { AdvanceFilterFieldType, AdvanceFilterOperator } from '@/types/types';
import { PEAdvanceFilters } from './PEAdvanceFilters';
import { ServiceResponse } from '@/types/service.types';
import { useTeamGetAll } from '@/services/teamService';
import Loader from '@/components/Loader';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ProcessStatus, WorkOrderResponse } from '@/types/workOrder.types';
import { Team } from '@/types/team.types';
import { useUserStore } from '@/stores/user';
import { ServiceSwitcher } from '@/components/ServiceSwitcher';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';

const canUserAssignToSelf = (workOrderTeamId: string | null, userTeams: Team[]) => {
  return userTeams.some((team) => team.id === workOrderTeamId);
};

const PEMaintenance = () => {
  const filterStore = useFilterStore((state) => state);
  const loggedInUser = useUserStore((state) => state.user);

  const { serviceFilters, setServiceFilters } = filterStore;
  const [searchParam, setSearchParam] = useSearchParams();
  const [openWoColumns, setOpenWoColumns] = useState<{ id: string; isExpanded: boolean }[]>([]);
  const methods = useForm();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [openProcessDetails, setOpenProcessDetails] = useState(false);
  const [openCommentSection, setOpenCommentSection] = useState(false);
  const [isAdvanceFilterOpen, setIsAdvanceFilterOpen] = useState(false);
  const [rows, setRows] = useState<ServiceResponse[]>([]);
  const [exportingWorkOrderId, setExportingWorkOrderId] = useState<string | null>(null);
  const exportingWorkOrderIdRef = useRef<string | null>(null);
  const workOrderPatchMutation = usePatchWorkOrder();
  const secondaryTableData = useMemo(() => {
    return rows.map((d) => {
      return { id: d.serviceId, data: d.workOrders };
    });
  }, [rows]);
  const { data: teams, isFetching: isFetchingTeams } = useTeamGetAll();

  const handleRowClick = (row: ServiceResponse, e: React.MouseEvent) => {
    const targetElement = e.target as HTMLElement;
    const closestActionElement = targetElement.closest('.action');
    if (!closestActionElement && !targetElement.classList.contains('modal')) {
      return;
    }
  };

  const statusClassMap = {
    CANCELED: 'bg-[#F5F5F5] text-[#676767] hover:bg-[#F5F5F5]',
    ASSIGNED: 'bg-[#EDF5FF] font-medium text-primary hover:bg-[#EDF5FF]',
    AWAITING: 'bg-[#FFEEBF] text-[#6C4A09] hover:bg-[#FFEEBF]',
  };

  const { setIsDirty } = useContext(NavigationBlockerContext);

  useEffect(() => {
    setIsDirty(false);
  }, []);

  const teamsOptions = useMemo(() => {
    return (
      teams?.teams.map((team) => ({
        label: team.name,
        value: team.id,
        team: team,
      })) ?? []
    );
  }, [teams]);
  const peSearchFields: DataTableSearchConfig[] = useMemo(
    () => [
      {
        label: 'Search Work Order Number',
        name: 'workOrderNumber',
        placeholder: 'Search Work Order Number',
      },
      {
        label: 'Search Client ID',
        placeholder: 'Search Client ID',
        name: 'clientId',
      },
      {
        label: 'Search Case Number',
        placeholder: 'Search Case Number',
        name: 'caseNumber',
      },
      {
        label: 'Search Server Name',
        placeholder: 'Search Server Name',
        name: 'serverName',
      },
      {
        label: 'Status',
        placeholder: 'Select Status',
        name: 'status',
        fieldType: 'SELECT',
        options: WORK_ORDER_STATUS_OPTIONS,
      },
      {
        label: 'Team',
        placeholder: 'Select Team',
        name: 'teamId',
        stickyItem: {
          label: 'My Teams',
          value: 'MY_TEAMS',
        },
        options: teamsOptions.map((team) => ({ label: team.label, value: team.value })),
        fieldType: 'SELECT',
      },
    ],
    [teamsOptions]
  );
  useEffect(() => {
    if (serviceFilters.length === 0) {
      setServiceFilters([
        {
          field: 'teamId',
          operator: AdvanceFilterOperator.EQUALS,
          value: 'MY_TEAMS',
          fieldType: AdvanceFilterFieldType.ID,
        },
      ]);
    }
  }, []);
  const columns: ColumnDef<ServiceResponse>[] = [
    {
      accessorKey: 'caseid',
      header: 'Case #',
      size: 100,
      cell: ({ row }) => {
        const rush = row?.original?.case?.rush;
        return (
          <div className={`${rush ? 'ml-[-10px]' : 'pl-1'} flex flex-row gap-1 items-center`}>
            {rush && <Flag className="text-[#D72222] w-3 h-3" fill="#D72222"></Flag>}
            <span className="min-w-[70px] mr-5">{row?.original?.case?.number ?? '-'}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Entry Date',
      enableSorting: true,
      cell: ({ cell }) => {
        return (
          <OneLineText text={convertISODateToMMDDYYYY(cell.row.original.createdAt)} className="min-w-[100px] mr-5" />
        );
      },
    },
    {
      accessorKey: 'caseName',
      header: 'Case Name ',
      enableSorting: true,
      size: 120,
      cell: ({ cell }) => {
        return <OneLineText text={cell.row.original.case?.name ?? '-'} className="min-w-[100px] mr-5" />;
      },
    },
    {
      accessorKey: 'fileNumber',
      header: 'File #',
      size: 100,
      cell: ({ cell }) => {
        return <OneLineText text={cell.row.original.case.fileNo ?? '-'} className="min-w-[100px] mr-5" />;
      },
    },

    {
      accessorKey: 'client',
      header: 'Client  ',
      size: 150,
      cell: ({ cell }) => {
        return <OneLineText text={cell.row.original?.client?.clientName ?? ''} className="min-w-[100px] mr-5" />;
      },
    },
    {
      accessorKey: 'actions',
      header: 'Action',
      size: 75,
      cell({ row }) {
        if (row.original.status === 'ASSIGNED' || row.original.status === 'AWAITING') {
          return (
            <span
              className="flex flex-row w-full gap-4 mr-5 min-w-[100px] cursor-pointer"
              onClick={() => {
                navigate(`${row.original.serviceId}/update`);
              }}
            >
              <Edit3 className="h-4 w-4 text-primary" />
            </span>
          );
        }
      },
    },
    {
      accessorKey: 'wo',
      header: '# W/O',
      cell: ({ cell }) => {
        const workOrders = cell.row.original.workOrders;
        const length = workOrders?.length ?? 0;
        const serviceId = cell.row.original.serviceId;
        return (
          <div className={`flex items-center flex-row min-w-[75px]`}>
            {openWoColumns.find((ow) => ow.id == serviceId) ? (
              <div
                className="flex flex-row gap-2"
                onClick={() => {
                  const filtered = openWoColumns.filter((wo) => wo.id !== serviceId);
                  setOpenWoColumns(filtered);
                }}
              >
                <span className="flex items-center">{length}</span>
                <div className="flex items-center">
                  <ChevronUp
                    color="#676767"
                    onClick={() => {
                      const filtered = openWoColumns.filter((wo) => wo.id !== serviceId);
                      setOpenWoColumns(filtered);
                    }}
                    className="h-4 w-4 flex align-middle text-center"
                  />
                </div>
              </div>
            ) : (
              <div
                className="flex flex-row gap-2"
                onClick={() => {
                  setOpenWoColumns((prev) => [...prev, { id: serviceId, isExpanded: true }]);
                }}
              >
                <span className="flex items-center">{length}</span>
                <div className="flex items-center">
                  <ChevronDown color="#676767" className="h-4 w-4 flex align-middle text-center" />
                </div>
              </div>
            )}
          </div>
        );
      },
      size: 75,
    },
  ];

  const workOrderColumns = (rows?: WorkOrderResponse[]): ColumnDef<WorkOrderResponse>[] => [
    {
      accessorKey: 'number',
      header: 'W/O No',
      size: 100,
      enableSorting: true,
    },
    {
      accessorKey: 'toServe',
      header: 'Person to Serve',
      size: 200,
      enableSorting: true,
      cell: ({ cell }) => {
        return <OneLineText text={cell.row.original.toServe} className="w-[200px] mr-5" />;
      },
    },
    {
      accessorKey: 'server',
      header: 'Server/Vendor',
      size: 200,
      cell: ({ row }) => {
        const serverName = displayFullName(row.original?.server ?? undefined) ?? '';
        const vendorName = row.original?.vendor?.vendor?.name;
        return <OneLineText className="w-[200px] mr-5" text={vendorName ? vendorName : serverName} />;
      },
    },
    {
      accessorKey: 'user',
      header: 'Assigned User',
      size: 200,
      cell: ({ row }) => {
        return <OneLineText className="w-[200px] mr-5" text={displayFullName(row.original?.user ?? undefined) ?? ''} />;
      },
    },
    {
      accessorKey: 'team',
      header: 'Team',
      size: 200,
      cell: ({ row }) => {
        return <OneLineText className="w-[200px] mr-5" text={row.original?.team?.name ?? '-'} />;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 200,
      cell: ({ row }) => {
        const status = structuredClone(row.original.status);
        return (
          <FormFieldSelect
            name={`status-${row.original.workOrderId}`}
            hasControl={false}
            isLoading={
              workOrderPatchMutation.status === 'pending' &&
              row.original.workOrderId === workOrderPatchMutation.variables?.workOrderId
            }
            options={WORK_ORDER_STATUS_OPTIONS}
            className={`h-[20px] w-[140px] text-xs font-medium mr-5 rounded-full ${statusClassMap[row.original.status] ?? ''}`}
            defaultValue={row.original.status}
            onChange={(value) => {
              if (value)
                workOrderPatchMutation.mutate(
                  {
                    workOrderId: row.original.workOrderId,
                    data: {
                      status: value as ProcessStatus,
                    },
                  },
                  {
                    onSuccess: (data) => {
                      const updatedData = data;
                      row.original.status = updatedData.status;
                      toast({
                        variant: 'default',
                        description: 'Work order status updated successfully.',
                        duration: 3000,
                      });
                    },

                    onError: (error) => {
                      row.original.status = status;
                      if (axios.isAxiosError(error) && error.response?.data) {
                        displayError(error.response.data, 5000);
                      } else {
                        toast({
                          variant: 'destructive',
                          description: 'Failed to update work order status.',
                          duration: 5000,
                        });
                      }
                    },
                  }
                );
            }}
          />
        );
      },
    },
    {
      accessorKey: 'actions',
      header: '',
      size: 200,
      cell: ({ row }) => {
        return (
          <div
            className="w-fit flex cursor-pointer min-w-[40px] flex-row gap-4 items-center"
            onClick={(e) => {
              e.stopPropagation();
            }}
            role="button"
          >
            <Download
              className={`h-5 w-5 text-primary ${
                exportingWorkOrderId === row.original.workOrderId
                  ? 'opacity-50 cursor-not-allowed pointer-events-none'
                  : ''
              }`}
              onClick={async () => {
                if (exportingWorkOrderIdRef.current === row.original.workOrderId) return;
                exportingWorkOrderIdRef.current = row.original.workOrderId;
                setExportingWorkOrderId(row.original.workOrderId);
                try {
                  await exportProcessServiceWorkOrder({
                    workOrderId: row.original.workOrderId,
                    workOrderNumber: row.original.number,
                    linkedHIWorkOrderID: row.original.linkedHIWorkOrderID,
                  });
                } finally {
                  exportingWorkOrderIdRef.current = null;
                  setExportingWorkOrderId(null);
                }
              }}
            />
            <div className="relative">
              <MessageSquarePlus
                className="h-5 w-5 text-primary"
                onClick={() => {
                  setOpenCommentSection(true);
                  setSelectedId(row.original.workOrderId);
                  setSelectedServiceId(row.original.serviceId);
                }}
              />
              {row.original._count?.comments ? (
                <span className="absolute -top-2 -right-2 flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[8px] font-semibold text-white bg-primary rounded-full">
                  {row.original._count?.comments}
                </span>
              ) : null}
            </div>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <div className="cursor-pointer">
                  <Ellipsis color="#676767" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border-[1px] border-bordercolor z-[1000] rounded-md max-h-[300px] overflow-auto custom-scrollbar">
                {canUserAssignToSelf(row.original?.teamId ?? null, loggedInUser?.teams ?? []) &&
                  row.original.userId !== loggedInUser?.userId && (
                    <>
                      <DropdownMenuCheckboxItem
                        className="!opacity-100 !font-semibold"
                        onClick={() => {
                          if (!loggedInUser) return;
                          workOrderPatchMutation.mutate(
                            {
                              workOrderId: row.original.workOrderId,
                              data: {
                                teamId: row.original?.teamId,
                                userId: loggedInUser?.userId,
                              },
                            },
                            {
                              onSuccess: () => {
                                toast({
                                  variant: 'default',
                                  description: 'Work order assigned to you successfully.',
                                  duration: 3000,
                                });
                              },

                              onError: (error) => {
                                if (axios.isAxiosError(error) && error.response?.data) {
                                  displayError(error.response.data, 5000);
                                } else {
                                  toast({
                                    variant: 'destructive',
                                    description: 'Failed to assign work order to you.',
                                    duration: 5000,
                                  });
                                }
                              },
                            }
                          );
                        }}
                      >
                        Assign Me
                      </DropdownMenuCheckboxItem>
                      <hr />
                    </>
                  )}
                <DropdownMenuCheckboxItem disabled className="!opacity-100 !font-semibold">
                  Assign Team
                </DropdownMenuCheckboxItem>
                <hr />
                {teamsOptions
                  .filter((team) => !['ADMIN', 'PROCESS_SERVER'].includes(team.team.key))
                  .map((team) => (
                    <DropdownMenuCheckboxItem
                      key={team.value}
                      checked={row.original?.teamId === team.value}
                      onClick={() => {
                        workOrderPatchMutation.mutate(
                          {
                            workOrderId: row.original.workOrderId,
                            data: {
                              teamId: team.value,
                            },
                          },
                          {
                            onSuccess: () => {
                              row.original.teamId = team.value;
                              row.original.team = team.team;
                              toast({
                                variant: 'default',
                                description: 'Work order team updated successfully.',
                                duration: 3000,
                              });
                            },

                            onError: (error) => {
                              if (axios.isAxiosError(error) && error.response?.data) {
                                displayError(error.response.data, 5000);
                              } else {
                                toast({
                                  variant: 'destructive',
                                  description: 'Failed to update work order team.',
                                  duration: 5000,
                                });
                              }
                            },
                          }
                        );
                      }}
                    >
                      <div className="flex flex-row justify-start items-center gap-2 w-full font-[500] text-3.5">
                        <span>{team.label}</span>
                      </div>
                    </DropdownMenuCheckboxItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
    {
      accessorKey: 'addWo',
      header: () => {
        return (
          <span
            className="text-[#3B82F6] text-end w-full cursor-pointer pr-6"
            onClick={() => {
              navigate(`${rows?.[0]?.serviceId}/update?stepper=W/O%20Details`);
            }}
          >
            + Add W/O
          </span>
        );
      },
      size: 75,
    },
  ];

  useEffect(() => {
    const searchParamWoId = searchParam.get('workOrderId');
    if (searchParamWoId) {
      setSelectedId(searchParamWoId);
      setOpenProcessDetails(true);
    }
  }, [searchParam]);

  return (
    <Loader isLoading={isFetchingTeams}>
      <div className="flex w-[100%]  ">
        <FormProvider {...methods}>
          <DataTable
            columns={columns}
            apiGet={getProcesses}
            openSecondaryColumns={openWoColumns}
            secondaryColumns={workOrderColumns}
            secondaryData={secondaryTableData}
            onRowClick={handleRowClick}
            onSecondaryRowClick={(row) => {
              setSearchParam({ workOrderId: row.workOrderId });
              setOpenProcessDetails(true);
              setSelectedId(row.workOrderId);
            }}
            toolbar={{
              toolBarTitle: 'Process Service Maintenance',

              addButton: true,
              addButtonTitle: 'New Process',
              addOnClick: () => navigate('/process/create?stepper=process'),
              serviceSwitcher: <ServiceSwitcher currentService="process" />,
              filters: {
                icon: <FilterIcon className="h-4 w-4 text-[#969696]" />,
                groups: [
                  {
                    label: 'rush',
                    name: 'shouldRush',
                    options: [
                      { label: 'All', value: null },
                      { label: 'Rush', value: 'true' },
                    ],
                  },
                ],
              },
            }}
            setRows={setRows}
            getSecondaryTableRowId={(row) => row.serviceId}
            onSearch={(search) => {
              if (!search) {
                setServiceFilters([]);
                return;
              }

              const updatedFilters = [...serviceFilters];

              Object.entries(search).forEach(([field, value]) => {
                const existingIndex = updatedFilters.findIndex((f) => f.field === field);

                if (!value) {
                  if (existingIndex !== -1) {
                    updatedFilters.splice(existingIndex, 1);
                  }
                } else {
                  if (existingIndex !== -1) {
                    updatedFilters[existingIndex] = {
                      ...updatedFilters[existingIndex],
                      value,
                    };
                  } else {
                    updatedFilters.push({
                      field,
                      value,
                      operator: field === 'serverName' ? AdvanceFilterOperator.CONTAINS : AdvanceFilterOperator.EQUALS,
                      fieldType: field === 'serverName' ? AdvanceFilterFieldType.STRING : AdvanceFilterFieldType.ID,
                    });
                  }
                }
              });

              setServiceFilters(updatedFilters);
            }}
            search={peSearchFields}
            advanceFilter={{
              filters: serviceFilters,
              setIsAdvanceFilterOpen: setIsAdvanceFilterOpen,
            }}
          />
        </FormProvider>
      </div>
      {openProcessDetails && selectedId && (
        <ProcessDetails
          onCloseClick={() => {
            setSearchParam({});
            setOpenProcessDetails(false);
          }}
          id={selectedId}
        />
      )}
      {openCommentSection && selectedId && selectedServiceId && (
        <PeCommentsDialog
          workOrderId={selectedId}
          onCloseClick={(count) => {
            setOpenCommentSection(false);
            setRows((prev) =>
              prev.map((row) => {
                if (row.serviceId === selectedServiceId) {
                  return {
                    ...row,
                    workOrders:
                      row.workOrders?.map((wo) => {
                        if (wo.workOrderId === selectedId) {
                          return { ...wo, _count: { ...wo._count, comments: count } };
                        }
                        return wo;
                      }) ?? [],
                  };
                }
                return row;
              })
            );
          }}
        />
      )}
      <PEAdvanceFilters
        onClose={() => setIsAdvanceFilterOpen(false)}
        filters={serviceFilters}
        setFilters={setServiceFilters}
        peListViewFilters={peSearchFields.map((field) => field.name)}
        open={isAdvanceFilterOpen}
      />
    </Loader>
  );
};

export default PEMaintenance;
